import { apparatusClass } from "apparatus/library";
import { Select } from "command/basic";
import { Sequence } from "command/command";
import { BreakGroup, GroupObjects } from "editing/grouping/commands";
import { getRootGroup } from "editing/grouping/operations";
import { Arrays } from "ext/array";
import appStore from "store/store";
import { t } from "i18next";
/**
 * Stores data about currently selected items and decisions about
 * the behaviour of the app based on those items.
 */
export class Selected {
    constructor(items, arrows) {
        var _a, _b;
        this.items = items;
        this.arrows = arrows;
        // Join all items.
        var allItems = [...items, ...arrows];
        // Check for consistent type.
        this.sameApparatusType = (_a = items[0]) === null || _a === void 0 ? void 0 : _a.type;
        for (const item of items) {
            if (item.type != items[0].type) {
                this.sameApparatusType = undefined;
                break;
            }
        }
        this.sameApparatusKind = ((_b = items[0]) === null || _b === void 0 ? void 0 : _b.type) ? apparatusClass(items[0].type).properties.kind : undefined;
        for (const item of items) {
            if (apparatusClass(item.type).properties.kind !== apparatusClass(items[0].type).properties.kind) {
                this.sameApparatusKind = undefined;
                break;
            }
        }
        this.isItemsWithLiquidOnly = this.arrows.length == 0 && this.items.every(i => i.liquid !== undefined);
        this.isItemsSupportingMeniscus = this.arrows.length == 0 && this.items.every(i => { var _a; return (_a = apparatusClass(i.type).properties.meniscus) !== null && _a !== void 0 ? _a : false; });
        var itemsNotInGroups = [];
        var arrowsNotInGroups = [];
        var rootGroupIds = [];
        var rootGroups = [];
        for (const item of allItems) {
            if (item.parentGroup) {
                const rootGroup = getRootGroup(item.parentGroup);
                if (!Arrays.includes(rootGroupIds, rootGroup.id)) {
                    rootGroupIds.push(rootGroup.id);
                    rootGroups.push(rootGroup);
                }
            }
            else {
                switch (item.id.type) {
                    case "apparatus":
                        itemsNotInGroups.push(item);
                        break;
                    case "arrow":
                        arrowsNotInGroups.push(item);
                        break;
                    case "group":
                        continue;
                }
            }
        }
        this.rootGroups = rootGroups;
        this.standaloneItems = itemsNotInGroups;
        this.standaloneArrows = arrowsNotInGroups;
    }
    getConsistentItemsLiquidColor() {
        var _a, _b, _c;
        const color = (_b = (_a = this.items[0].liquid) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : "";
        if (this.items.length == 1)
            return color;
        for (const item of this.items) {
            if (((_c = item.liquid) === null || _c === void 0 ? void 0 : _c.color) !== color)
                return;
        }
        return color;
    }
    get isEmpty() {
        return this.items.length == 0 && this.arrows.length == 0;
    }
    get standaloneObjectsCount() {
        return this.standaloneArrows.length + this.standaloneItems.length;
    }
    get groupsCount() {
        return this.rootGroups.length;
    }
    /** Returns the display string, e.g. "2 groups & 1 item" */
    get displayString() {
        if (this.groupsCount == 0) {
            return t("ui.item", { count: this.standaloneObjectsCount });
        }
        if (this.standaloneObjectsCount == 0) {
            return t("ui.group", { count: this.groupsCount });
        }
        return `${t("ui.group", { count: this.groupsCount })} & ${t("ui.item", { count: this.standaloneObjectsCount })}`;
    }
    get shouldAllowGrouping() { return this.standaloneObjectsCount + this.groupsCount >= 2; }
    get shouldAllowUngrouping() { return this.groupsCount == 1 && this.standaloneObjectsCount == 0; }
    get shouldAllowRegrouping() {
        return (this.items.length == 1)
            && Selected.lastRegroupData.ids.length > 0
            && Arrays.includes(Selected.lastRegroupData.ids, this.items[0].id);
    }
    /** Returns a command to group selected objects. Returns undefined on no-op. */
    createGroupObjectsCommand() {
        if (!this.shouldAllowGrouping)
            return;
        Selected.lastRegroupData.ids = [];
        const ids = [...this.rootGroups, ...this.standaloneArrows, ...this.standaloneItems].map(i => i.id);
        return new GroupObjects(ids);
    }
    /** Returns a command to ungroup selected objects. Returns undefined on no-op. */
    createUngroupObjectsCommand() {
        if (!this.shouldAllowUngrouping)
            return;
        Selected.lastRegroupData.ids = this.rootGroups[0].items;
        return new BreakGroup(this.rootGroups[0]);
    }
    /** Returns a command to re-group selected item. Returns undefined on no-op. */
    createRegroupObjectsCommand() {
        if (!this.shouldAllowRegrouping)
            return;
        const existingIds = appStore.filterExisting(Selected.lastRegroupData.ids);
        // Remove Ids of objects that have already been selected.
        const selecteIds = [...this.items, ...this.arrows].map(i => i.id);
        const toSelectIds = existingIds.filter(id => !Arrays.includes(selecteIds, id));
        Selected.lastRegroupData.ids = [];
        // Select unselected items and then regroup items.
        return new Sequence([
            new Select(toSelectIds),
            new GroupObjects(existingIds)
        ]);
    }
}
Selected.lastRegroupData = { ids: [] };
